// Vue "Vue d'ensemble" : icicle d3.partition sur tout le parc filtre, bandes
// Client / Serveur / Environnement / Module / Source ; largeur proportionnelle au nombre de feuilles.
import * as d3 from "d3";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { Puces } from "../utils/PageEntete";
import useResize from "../../hooks/useResize";
import { BulleFlottante, useBulle } from "../Logs/BulleFlottante";
import { tronquer } from "../Logs/VueTreemap";
import { STATUTS, construireGlacon, pireStatutCarto } from "./cartographieDonnees";
import { LegendeStatuts } from "./StatutsCarto";

// Hauteur de base d'une bande par profondeur (Parc, Client, Serveur, Env, Module, Source) ;
// la vue occupe au moins HAUTEUR_MIN pixels (bandes etirees).
const HAUTEURS = [36, 44, 44, 60, 60, 60];
const HAUTEUR_MIN = 520;
const NIVEAUX = ["Parc", "Client", "Serveur", "Environnement", "Module", "Source"];
const PALETTE = [
  "#195c84", "#ff8801", "#2e9e6b", "#8b5fbf", "#d94f70", "#17a2b8",
  "#c9a227", "#6c7a89", "#5b8c2a", "#b5651d", "#3f51b5", "#a0522d",
];
const AUTRES = "#b8c0cc";
const NEUTRES = { root: "#012b43", client: "#195c84", server: "#5b86a3", env: "#9db6c8" };

export default function VueGlacon({ clients }) {
  const [couleur, setCouleur] = useState("prestataire");
  const [focusId, setFocusId] = useState("root");
  const conteneur = useRef();
  const svgRef = useRef();
  const size = useResize(conteneur);
  const { bulle, montrer, cacher } = useBulle();
  const montrerRef = useRef(montrer);
  const cacherRef = useRef(cacher);
  montrerRef.current = montrer;
  cacherRef.current = cacher;
  const largeur = (size && size.width) || 900;

  const racine = useMemo(() => {
    const r = d3
      .hierarchy(construireGlacon(clients))
      .sum((d) => (d.children ? 0 : 1));
    d3.partition().size([1, NIVEAUX.length])(r);
    r.eachAfter((n) => {
      n.statutAgrege = n.children ? pireStatutCarto(n.children.map((c) => c.statutAgrege)) : pireStatutCarto([n.data.statut || "no_activity"]);
      n.nbEnv = n.data.type === "env" ? 1 : n.children ? d3.sum(n.children, (c) => c.nbEnv) : 0;
    });
    return r;
  }, [clients]);

  // Legende des prestataires : ~12 plus frequents (sur les feuilles source), le reste en gris.
  const prestataires = useMemo(() => {
    const cpt = new Map();
    racine.leaves().forEach((f) => {
      if (f.data.source) {
        const k = f.data.source.toLowerCase();
        const cur = cpt.get(k) || { nom: f.data.source, n: 0 };
        cur.n += 1;
        cpt.set(k, cur);
      }
    });
    const tries = [...cpt.entries()].sort((a, b) => b[1].n - a[1].n || a[0].localeCompare(b[0]));
    return { top: tries.slice(0, 12).map(([k, v]) => ({ cle: k, nom: v.nom, n: v.n })), autres: tries.length > 12 };
  }, [racine]);

  const focus = racine.descendants().find((n) => n.data.id === focusId) || racine;
  const chemin = focus.ancestors().reverse();

  const couleurNoeud = (d) => {
    if (couleur === "statut") return STATUTS[d.statutAgrege].couleur;
    if (d.data.type === "source" || d.data.type === "module") {
      // prestataire observe s'il existe, sinon declare
      const f = d.data.type === "source" ? d : d.leaves().find((l) => l.data.observe) || d.leaves().find((l) => l.data.source);
      const src = f && f.data.source ? f.data.source.toLowerCase() : null;
      if (!src) return AUTRES;
      const i = prestataires.top.findIndex((p) => p.cle === src);
      return i >= 0 ? PALETTE[i] : AUTRES;
    }
    return NEUTRES[d.data.type] || AUTRES;
  };

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const base = HAUTEURS.slice(focus.depth);
    const k = Math.max(1, HAUTEUR_MIN / base.reduce((a, b) => a + b, 0));
    const hauteurs = HAUTEURS.map((h) => h * k);
    const decalage = (depth) => hauteurs.slice(focus.depth, depth).reduce((a, b) => a + b, 0);
    const hauteur = hauteurs.slice(focus.depth).reduce((a, b) => a + b, 0);
    svg.attr("width", largeur).attr("height", hauteur);
    const echelle = d3.scaleLinear().domain([focus.x0, focus.x1]).range([0, largeur]);
    const noeuds = focus.descendants().filter((d) => echelle(d.x1) - echelle(d.x0) >= 1);

    const geo = (d) => ({
      x: echelle(d.x0),
      y: decalage(d.depth),
      h: hauteurs[d.depth] - 2,
      w: Math.max(0, echelle(d.x1) - echelle(d.x0) - 1),
    });
    const groupes = svg
      .selectAll("g.bande")
      .data(noeuds, (d) => d.data.id)
      .join(
        (enter) => {
          const e = enter.append("g").attr("class", (d) => `bande bande-${d.data.type}`);
          e.append("rect")
            .attr("rx", 3)
            .attr("height", (d) => geo(d).h)
            .attr("x", (d) => geo(d).x)
            .attr("y", (d) => geo(d).y)
            .attr("width", (d) => geo(d).w);
          e.append("text").attr("dy", "0.35em").attr("font-size", 13).attr("fill", "#fff").style("pointer-events", "none");
          return e;
        },
        (update) => update,
        (exit) => exit.remove()
      );
    groupes
      .attr("data-id", (d) => d.data.id)
      .style("cursor", (d) => (d.children ? "pointer" : "default"))
      .on("click", (ev, d) => {
        if (d.children) setFocusId(d.data.id);
      })
      .on("mousemove", (ev, d) =>
        montrerRef.current(
          ev,
          <div>
            <b>
              {d
                .ancestors()
                .reverse()
                .slice(1)
                .map((a) => a.data.name)
                .join(" › ") || "Parc"}
            </b>
            <br />
            {d.data.source ? `Prestataire : ${d.data.source}${d.data.anterieure ? " (antérieur)" : d.data.observe ? " (observé)" : " (déclaré)"}` : null}
            {d.data.source ? <br /> : null}
            {STATUTS[d.statutAgrege].label}
            <br />
            {d.nbEnv} env.
          </div>
        )
      )
      .on("mouseleave", () => cacherRef.current());
    groupes
      .select("rect")
      .attr("fill", couleurNoeud)
      .transition()
      .duration(350)
      .attr("x", (d) => geo(d).x)
      .attr("y", (d) => geo(d).y)
      .attr("width", (d) => geo(d).w)
      .attr("height", (d) => geo(d).h);
    groupes
      .select("text")
      .attr("x", (d) => geo(d).x + 6)
      .attr("y", (d) => geo(d).y + geo(d).h / 2)
      .text((d) => tronquer(d.data.name, geo(d).w - 8));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [racine, focus, couleur, largeur, prestataires]);

  if (!racine.children) return <div className="carto-vide">Aucun résultat pour ces filtres</div>;

  return (
    <div data-testid="vue-glacon">
      <div className="carto-glacon-tete">
        <nav aria-label="Fil d'Ariane" className="carto-ariane">
          {chemin.map((n, i) => (
            <span key={n.data.id}>
              {i > 0 && " › "}
              {i < chemin.length - 1 ? (
                <button type="button" className="carto-lien" onClick={() => setFocusId(n.data.id)}>
                  {n.data.name}
                </button>
              ) : (
                <b>{n.data.name}</b>
              )}
            </span>
          ))}
        </nav>
        <Puces
          inline
          label="Couleur"
          items={[
            { key: "prestataire", label: "Prestataire" },
            { key: "statut", label: "Statut" },
          ]}
          value={couleur}
          onChange={setCouleur}
        />
      </div>
      <div className="carto-glacon-svg" ref={conteneur}>
        <svg ref={svgRef} role="img" aria-label="Vue d'ensemble du parc" />
        <BulleFlottante bulle={bulle} />
      </div>
      <div className="carto-legende" data-testid="legende-glacon">
        {NIVEAUX.slice(focus.depth).join(" / ")} ·
        {couleur !== "statut" && (
          <>
            {prestataires.top.map((p, i) => (
              <LegendeItem key={p.cle} c={PALETTE[i]} t={`${p.nom} (${p.n})`} />
            ))}
            {prestataires.autres && <LegendeItem c={AUTRES} t="autres" />}
          </>
        )}
      </div>
      {couleur === "statut" && <LegendeStatuts />}
    </div>
  );
}

function LegendeItem({ c, t }) {
  return (
    <span>
      <span className="carto-dot" style={{ background: c }} />
      {t}
    </span>
  );
}
